import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import MessageList from 'Components/ComponentLibrary/Organisms/MessageList';
import { scrollToRef } from 'Helpers/ScrollToRef';
import useMobileLayout from 'Hooks/useMobileLayout';
import {
  useMessageInput,
  MessageInput,
} from 'Components/ComponentLibrary/Components/Uncategorized/MessageInput';
import {
  ThreadHeader,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadHeader';
import { DataValueMap } from 'Helpers/Testing';
import {
  OutOfOfficeNoticeDialog,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/OutOfOfficeNoticeDialog';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import fr from 'facet-rest';
import Sentry from 'Integrations/Sentry';
import { useManualRoutingSubmit } from 'Hooks/useRefreshEvent';
import ThreadRoutingDetails
  from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel/components/MessagesPanelContent/components/ThreadRoutingDetails';
import { FDBox } from '@facet-wealth/design';

const useStyles = buildUseStyles('MessagesPanelContent', (theme) => ({
  messageList: {
    overflowY: 'auto',
    overflowX: 'clip',
  },
  spacer: {
    flexGrow: 1,
  },
  input: {
    padding: theme.spacing(2),
  },
}));

const MessagesPanelContent = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const scrollMessageRef = useRef();
  const [contentToSubmit, setContentToSubmit] = useState('');

  const {
    selectedThreadResource,
    selectedThreadRoutingDetailsResource,
    messagesList,
    threadDraftMessageResource,
    refreshThreadDraftMessage,
    refreshAll,
    sendThreadReply,
    autoSaveThreadDraft,
    messageSending,
    setOutOfOfficeNotificationResource,
  } = useMessagesContext();

  useManualRoutingSubmit(async () => {
    await refreshAll();
  }, []);

  const {
    dialogOpen,
    loading: dialogLoading,
    handleSubmit: handleConfirmationDialogSubmit,
    handleOpen,
    handleClose,
  } = useConfirmationDialogState({});

  const {
    handleSubmit,
    handleContentChange,
    handleAttachmentDelete,
    handleTemplateInsert,
  } = useMessageInput(
    threadDraftMessageResource,
    sendThreadReply,
    refreshThreadDraftMessage,
    autoSaveThreadDraft,
  );

  const readOnly = !threadDraftMessageResource;

  // Auto scroll to the bottom of the message chain on or new message
  const onAllMessagesLoaded = () => {
    scrollToRef(scrollMessageRef, { behavior: 'smooth' });
  };

  const submitMessageInput = async (value) => {
    try {
      // we only want to open popup for members when ALL planners on thread are out of office
      if (selectedThreadResource.relationship(fr.relationships.outOfOffice.name)) {
        const [outOfOfficeNotificationResource] = await selectedThreadResource.relationship(fr.relationships.outOfOffice.name).link('related').get();
        setContentToSubmit(value);
        setOutOfOfficeNotificationResource(outOfOfficeNotificationResource);
        handleOpen();
        return;
      }
    } catch (err) {
      // if fetch for out of office notification fails, log error and send message
      Sentry.createSentryErrorLog(err, err?.status);
    }
    await handleSubmit(value);
  };


  return (
    <React.Fragment>
      {selectedThreadRoutingDetailsResource
        ? (
          <FDBox paddingY={1} paddingX={2}>
            <ThreadRoutingDetails resource={selectedThreadRoutingDetailsResource} />
          </FDBox>
        ) : null}
      <ThreadHeader className={classNames({ [classes.mobileHeader]: isMobile })} />
      <Box
        className={classes.messageList}
        {...DataValueMap.init('messages-page-messages').build()}
      >
        <MessageList
          messageResources={messagesList}
          enableMessageEditing
          onMessageLoadComplete={() => onAllMessagesLoaded()}
          scrollRef={scrollMessageRef}
        />
      </Box>
      {/* Do not remove or change this empty div below! This is a spacer component meant to push the content */}
      <Box className={classes.spacer} />
      {/* Do not remove or change this empty div above! This is a spacer component meant to push the content */}
      <Box>
        <MessageInput
          className={classNames(classes.input, { [classes.mobileMessageInput]: isMobile })}
          onSubmit={(value) => submitMessageInput(value)}
          onChange={(value) => handleContentChange(value)}
          handleTemplateInsert={(value) => handleTemplateInsert(value)}
          handleAttachmentDelete={handleAttachmentDelete}
          messageSending={messageSending}
          messageResource={threadDraftMessageResource}
          refreshMessages={refreshThreadDraftMessage}
          readOnly={readOnly}
          value={threadDraftMessageResource?.attr('content')}
          dataTestAttributes={DataValueMap.init('messages-page-thread-input').build()}
          placeholder={'Write a new message'}
          rounded
        />
      </Box>
      {dialogOpen && (
      <OutOfOfficeNoticeDialog
        handleConfirm={handleConfirmationDialogSubmit}
        handleClose={handleClose}
        loading={dialogLoading}
        handleSubmit={handleSubmit}
        value={contentToSubmit}
      />
      )}
    </React.Fragment>
  );
};

export { MessagesPanelContent };
